import React, { useState, useEffect, useRef } from 'react';
import White from '../../assets/icons/popup/coin wallet.svg?react';
import Withdraw from '../../assets/icons/Withdraw.svg?react';
import BgLatto from '../../assets/images/bg-latto.jpg';
import Wallet from '../../assets/icons/popup/input-walet.svg?react';
import Spinner from './Spinner';
import DepositSuccessPopup from './DepositSuccessPopup';
import DepositConfirmationPopup from './DepositConfirmationPopup';
import payApi from '../../api/pay';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import Swal from 'sweetalert2';

import { settingsApi } from "../../api/settings";

// Import Gateway Logos
import GCashLogo from "../../assets/icons/popup/GCASH-Logo.svg?react";
import MayaLogo from "../../assets/icons/popup/MAYA-Logo.svg?react";
import QRPHLogo from "../../assets/icons/popup/QR_Ph_Logo.svg?react";
import QRPHLogoPlus from "../../assets/icons/popup/QR_Ph_Logo_Plus.svg?react";

import GCashPlusLogo from "../../assets/icons/popup/gcash-plus-large.svg?react";
import PayWalletLogo from "../../assets/icons/popup/paywallet-logo.svg?react";
import PayWalletXLogo from "../../assets/icons/popup/paywalletx-logo.svg?react";

const PHMPAY_GATEWAYS = {
  phmpay_qrph: {
    payMethod: 'PH_QRPH_DYNAMIC',
    fallbackMinAmountKey: 'phmpay',
  },
  phmpay_gcashplus: {
    payMethod: 'PH_GCASH_URL',
    fallbackMinAmountKey: 'phmpay',
  },
};

const normalizeGatewayKeys = (gatewayKeys = []) => {
  const normalized = gatewayKeys.flatMap((gateway) => {
    if (gateway === 'phmpay') {
      return ['phmpay_qrph'];
    }

    return gateway;
  });

  return [...new Set(normalized)];
};

const getGatewayMinAmount = (minAmounts, gatewayKey, defaultMinAmount) => {
  if (minAmounts?.[gatewayKey]) {
    return minAmounts[gatewayKey];
  }

  const fallbackMinAmountKey = PHMPAY_GATEWAYS[gatewayKey]?.fallbackMinAmountKey;
  if (fallbackMinAmountKey && minAmounts?.[fallbackMinAmountKey]) {
    return minAmounts[fallbackMinAmountKey];
  }

  if (gatewayKey === 'paywalletx' && minAmounts?.paywallet) {
    return minAmounts.paywallet;
  }

  if (gatewayKey === 'paywallet_gcash' && minAmounts?.paywallet) {
    return minAmounts.paywallet;
  }

  if (gatewayKey === 'paywallet_storeqr' && minAmounts?.paywallet) {
    return minAmounts.paywallet;
  }

  return defaultMinAmount;
};

const PAYMENT_METHODS_CONFIG = {
  gcash: {
    label: "GCash",
    Logo: GCashLogo,
    apiCallKey: 'pay', // Key to map to payApi functions
    defaultMinAmount: 50,
    backgroundColor: '#FFFFFF',
  },
  maya: {
    label: "QRPH+", // maya ni sya ha
    Logo: QRPHLogoPlus,
    apiCallKey: 'payMaya',
    defaultMinAmount: 50,
    backgroundColor: '#FFFFFF',
  },
  qrph: {
    label: "QRPH",
    Logo: QRPHLogo,
    apiCallKey: 'pay', // Assuming QRPH uses the same generic 'pay' endpoint
    defaultMinAmount: 2000,
    backgroundColor: '#FFFFFF',
  },
  phmpay_qrph: {
    label: "QRPH",
    Logo: QRPHLogo,
    apiCallKey: 'payPhmpay',
    defaultMinAmount: 50,
    backgroundColor: '#FFFFFF',
  },
  phmpay_gcashplus: {
    label: "GCash+",
    Logo: GCashPlusLogo,
    apiCallKey: 'payPhmpay',
    defaultMinAmount: 50,
    backgroundColor: '#5698C6',
    textColor: '#FFFFFF',
  },
  paywallet: {
    label: "QRPH 1",
    Logo: PayWalletLogo,
    apiCallKey: 'payPaywallet',
    defaultMinAmount: 50,
    cardVariant: 'featured',
    background: 'linear-gradient(145deg, #064e3b 0%, #0d9488 50%, #2dd4bf 100%)',
    textColor: '#FFFFFF',
    boxShadow: '0 8px 24px rgba(20, 184, 166, 0.4)',
  },
  paywalletx: {
    label: "QRPH 2",
    Logo: PayWalletXLogo,
    apiCallKey: 'payPaywallet',
    defaultMinAmount: 50,
    cardVariant: 'featured',
    background: 'linear-gradient(145deg, #4c1d95 0%, #7c3aed 50%, #c4b5fd 100%)',
    textColor: '#FFFFFF',
    boxShadow: '0 8px 24px rgba(124, 58, 237, 0.45)',
  },
  paywallet_gcash: {
    label: "GCASH-NOW",
    Logo: GCashPlusLogo,
    apiCallKey: 'payPaywallet',
    defaultMinAmount: 50,
    cardVariant: 'featured',
    background: 'linear-gradient(145deg, #0c4a6e 0%, #0284c7 50%, #7dd3fc 100%)',
    textColor: '#FFFFFF',
    boxShadow: '0 8px 24px rgba(2, 132, 199, 0.45)',
  },
  // TapIt store QR (PH_QRPH_STATIC_4): static QR, the player types the amount
  paywallet_storeqr: {
    label: "QRPH 3",
    Logo: PayWalletLogo,
    apiCallKey: 'payPaywallet',
    defaultMinAmount: 50,
    cardVariant: 'featured',
    background: 'linear-gradient(145deg, #7c2d12 0%, #ea580c 50%, #fdba74 100%)',
    textColor: '#FFFFFF',
    boxShadow: '0 8px 24px rgba(234, 88, 12, 0.4)',
  },
};

// QRPH options to suggest when every store QR is busy
const STORE_QR_FALLBACK_GATEWAYS = ['paywallet', 'paywalletx', 'phmpay_qrph'];

const getGatewayCardClassName = (config) => {
  if (config?.cardVariant === 'featured') {
    return 'relative flex flex-col items-center justify-center p-3 rounded-xl border-2 border-white/30 transition-all duration-200 hover:scale-[1.04] hover:border-white/50 active:scale-[0.98] min-h-[118px] overflow-hidden';
  }

  return 'flex flex-col items-center justify-center p-4 rounded-xl border border-gray-200 bg-white transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02]';
};

const ALTERNATIVE_DEPOSIT_GATEWAYS = ['phmpay_gcashplus', 'paywallet', 'paywalletx', 'paywallet_gcash', 'paywallet_storeqr'];

const formatGatewayLabelList = (gatewayKeys) => {
  const labels = gatewayKeys
    .map((key) => PAYMENT_METHODS_CONFIG[key]?.label)
    .filter(Boolean);

  if (labels.length <= 1) {
    return labels[0] || '';
  }

  if (labels.length === 2) {
    return `${labels[0]} or ${labels[1]}`;
  }

  return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
};

const getGatewayCardStyle = (config) => {
  if (config?.cardVariant === 'featured') {
    return {
      background: config.background,
      color: config.textColor,
      boxShadow: config.boxShadow,
    };
  }

  return {
    backgroundColor: config.backgroundColor || '#FFFFFF',
  };
};

export default function DepositPopupQR({ isOpen, onClose, currentBalance }) {
  const [amount, setAmount] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { user, tokens } = useAuth();
  const { resetPaymentStatus, refreshWalletBalance } = useWebSocket();
  const [paymentSuccessData, setPaymentSuccessData] = useState([]);
  
  const [availableGateways, setAvailableGateways] = useState([]);
  const [gatewayMinAmounts, setGatewayMinAmounts] = useState({});
  const [isLoadingGateways, setIsLoadingGateways] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [showGatewaySelection, setShowGatewaySelection] = useState(false);

  const getCurrentMinAmount = () => {
    if (selectedGateway) {
      return gatewayMinAmounts[selectedGateway] || PAYMENT_METHODS_CONFIG[selectedGateway]?.defaultMinAmount || 50;
    }
    
    return 50;
  };

  const wasOpenRef = useRef(false);
  useEffect(() => {
    const fetchGateways = async () => {
      if (isOpen && tokens?.access?.token && user?.id) {
        setIsLoadingGateways(true);
        try {
          const response = await settingsApi.getSettingsByKey(tokens.access.token, 'activePaymentGateway');
          const otherSettings = response.data?.otherSettings || response.data?.other_settings;
          const activeGatewaysList = normalizeGatewayKeys(otherSettings?.gateways || []);
          
          if (Array.isArray(activeGatewaysList) && activeGatewaysList.length > 0) {
            const supportedGateways = activeGatewaysList.filter(gw => PAYMENT_METHODS_CONFIG[gw]);
            setAvailableGateways(supportedGateways);
            
            const minAmounts = {};
            supportedGateways.forEach(gw => {
              minAmounts[gw] = getGatewayMinAmount(
                otherSettings?.minAmounts,
                gw,
                PAYMENT_METHODS_CONFIG[gw]?.defaultMinAmount || 50
              );
            });
            setGatewayMinAmounts(minAmounts);
            
            if (supportedGateways.length === 0 && activeGatewaysList.length > 0) {
                Swal.fire('Configuration Issue', 'Some payment methods returned by the server are not configured in the UI.', 'warning');
            }
          } else {
            setAvailableGateways([]);
            setGatewayMinAmounts({});
          }
        } catch (error) {
          console.error("Failed to fetch payment gateways:", error);
          setAvailableGateways([]);
          setGatewayMinAmounts({});
          // Swal.fire('Error', 'Could not load payment methods. Please try again later.', 'error');
        } finally {
          setIsLoadingGateways(false);
        }
      }
    };

    if (isOpen && !wasOpenRef.current) {
      resetPaymentStatus();
      setAmount(50);
      setSelectedGateway(null);
      setShowGatewaySelection(true);
      setShowConfirmation(false);
      setShowSuccess(false);
      fetchGateways();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, tokens, user, resetPaymentStatus]);

  useEffect(() => {
    if (selectedGateway) {
      const minAmount = getCurrentMinAmount();
      setAmount(minAmount);
    }
  }, [selectedGateway]);

  if (!isOpen) return null;

  const handleActualDeposit = async () => {
    if (!selectedGateway) {
      Swal.fire('Error', 'No payment gateway selected.', 'error');
      return;
    }

    setShowConfirmation(false); // Close confirmation before loading
    setIsLoading(true);
    const data = {
      amount: amount,
      currency: 'PHP',
      name: user.name,
      email: user.email,
    };

    // Only add gateway field for non-phmpay payments
    if (!PHMPAY_GATEWAYS[selectedGateway] && selectedGateway !== 'paywallet' && selectedGateway !== 'paywalletx' && selectedGateway !== 'paywallet_gcash' && selectedGateway !== 'paywallet_storeqr') {
      data.gateway = selectedGateway;
    }

    if (selectedGateway === 'paywallet') {
      data.paymentMethod = 'QRPH';
    }

    if (selectedGateway === 'paywalletx') {
      data.paymentMethod = 'PH_QRPH_DYNAMIC_2';
    }

    if (selectedGateway === 'paywallet_gcash') {
      data.paymentMethod = 'PH_GCASH_URL';
    }

    if (selectedGateway === 'paywallet_storeqr') {
      data.paymentMethod = 'PH_QRPH_STATIC_4';
    }

    if (PHMPAY_GATEWAYS[selectedGateway]) {
      data.payMethod = PHMPAY_GATEWAYS[selectedGateway].payMethod;
      data.gateway = selectedGateway;
    }

    try {
      let response;
      const methodConfig = PAYMENT_METHODS_CONFIG[selectedGateway];

      if (!methodConfig) {
        throw new Error('Invalid payment method configuration.');
      }

      if (methodConfig.apiCallKey === 'payMaya') {
        response = await payApi.payMaya(tokens.access.token, data);
      } else if (methodConfig.apiCallKey === 'payPhmpay') {
        response = await payApi.payPhmpay(tokens.access.token, data);
      } else if (methodConfig.apiCallKey === 'payPaywallet') {
        response = await payApi.payPaywallet(tokens.access.token, data);
      } else if (methodConfig.apiCallKey === 'pay') {
        // This assumes payApi.pay is for GCash/QRPH or a generic HitPay like service
        response = await payApi.pay(tokens.access.token, data);
      } else {
        throw new Error(`No API call defined for ${selectedGateway}`);
      }
      
      if (response.status === 200) {
        console.log('API Response:', response.data);
        console.log('Selected Gateway:', selectedGateway);
        
        // Handle Maya-specific response format
        if (selectedGateway === 'maya') {
          // For Maya, the response includes redirectUrl, paymentId, and qrCode
          const mayaData = {
            ...response.data,
            gateway: 'maya',
            amount: amount,
            currency: 'PHP'
          };
          console.log('Maya Data being set:', mayaData);
          setPaymentSuccessData(mayaData);
          
        } else if (PHMPAY_GATEWAYS[selectedGateway]) {
          // For Philippines wallet, handle the response format
          const phmpayData = {
            ...response.data,
            gateway: selectedGateway,
            amount: amount,
            currency: 'PHP'
          };
          console.log('Phmpay Data being set:', phmpayData);
          setPaymentSuccessData(phmpayData);

        } else if (selectedGateway === 'paywallet') {
          const paywalletData = {
            ...response.data,
            gateway: 'paywallet',
            amount: amount,
            currency: 'PHP'
          };
          console.log('PayWallet Data being set:', paywalletData);
          setPaymentSuccessData(paywalletData);

        } else if (selectedGateway === 'paywalletx') {
          const paywalletxData = {
            ...response.data,
            gateway: 'paywalletx',
            amount: amount,
            currency: 'PHP'
          };
          console.log('PayWalletX Data being set:', paywalletxData);
          setPaymentSuccessData(paywalletxData);

        } else if (selectedGateway === 'paywallet_gcash') {
          const paywalletGcashData = {
            ...response.data,
            gateway: 'paywallet_gcash',
            amount: amount,
            currency: 'PHP'
          };
          console.log('PayWallet GCash Data being set:', paywalletGcashData);
          setPaymentSuccessData(paywalletGcashData);

        } else if (selectedGateway === 'paywallet_storeqr') {
          const paywalletStoreQrData = {
            ...response.data,
            gateway: 'paywallet_storeqr',
            amount: amount,
            currency: 'PHP'
          };
          setPaymentSuccessData(paywalletStoreQrData);
          
        } else {
          // For other gateways, use the original response data
          setPaymentSuccessData(response.data);
        }
        setShowSuccess(true);
      } else if (
        selectedGateway === 'paywallet_storeqr' &&
        (response.status === 503 || response.data?.code === 'STORE_QR_BUSY')
      ) {
        // Every store QR in the shared pool is held by another deposit
        const fallbackGateways = STORE_QR_FALLBACK_GATEWAYS.filter((gateway) =>
          availableGateways.includes(gateway)
        );
        const fallbackNames = formatGatewayLabelList(fallbackGateways);
        const result = await Swal.fire({
          title: `${PAYMENT_METHODS_CONFIG.paywallet_storeqr.label} is busy`,
          text: `${PAYMENT_METHODS_CONFIG.paywallet_storeqr.label} is busy right now. Please try again in a minute${fallbackNames ? `, or use ${fallbackNames}` : ''}.`,
          icon: 'info',
          showCancelButton: fallbackGateways.length > 0,
          confirmButtonText: 'OK',
          cancelButtonText: 'Choose another method',
        });
        if (result.dismiss === Swal.DismissReason.cancel) {
          setSelectedGateway(null);
          setShowGatewaySelection(true);
        }
      } else {
        Swal.fire('Payment Failed', response.data?.message || 'The payment could not be processed.', 'error');
      }
    } catch (error) {
      console.error("Payment API call error:", error);
      Swal.fire('Payment Error', error.message || 'An unexpected error occurred during payment.', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDepositButtonClick = () => {
    const numericAmount = parseFloat(amount);
    const minAmount = getCurrentMinAmount();
    
    if (isNaN(numericAmount) || numericAmount < minAmount) {
      Swal.fire({
        title: 'Invalid Amount',
        text: `Amount must be at least ₱${minAmount.toLocaleString()}.`,
        icon: 'error',
      });
      return;
    }

    // Since we already have a selected gateway, go directly to confirmation
    setShowConfirmation(true);
  };

  const handleGatewaySelect = (gatewayKey) => {
    setSelectedGateway(gatewayKey);
    setShowGatewaySelection(false);
    // Don't go to confirmation yet, show amount input instead
  };

  const handleClose = () => {
    setShowSuccess(false); // Close success popup
    onClose(); // Call original onClose to close the main deposit modal
  };

  if (isLoading) { // Full screen spinner for critical operations
    return (
      <div className='fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60] font-poppins'>
        <Spinner />
      </div>
    );
  }
  
  if (showSuccess) {
    return (
      <DepositSuccessPopup
        amount={amount}
        onClose={handleClose} // Use updated handleClose
        paymentSuccessData={paymentSuccessData}
      />
    );
  }

  if (showConfirmation) {
    return (
      <DepositConfirmationPopup
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleActualDeposit} // Use the renamed function
        amount={amount}
        gatewayName={PAYMENT_METHODS_CONFIG[selectedGateway]?.label || selectedGateway}
      />
    );
  }

  if (showGatewaySelection) {
    const alternativeDepositGateways = ALTERNATIVE_DEPOSIT_GATEWAYS.filter((gateway) =>
      availableGateways.includes(gateway)
    );
    const missingMethods = ['gcash', 'maya', 'qrph'].filter(
      (method) => !availableGateways.includes(method)
    );
    const shouldShowInfoMessage =
      alternativeDepositGateways.length > 0 && missingMethods.length > 0;
    const alternativeMethodNames = formatGatewayLabelList(alternativeDepositGateways);

    return (
      <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 font-poppins'>
        <div className='animate-slideInAndShake relative w-full max-w-md bg-gradient-to-br from-slate-800 to-slate-900 shadow-2xl rounded-2xl transform transition-all duration-300'>
          <div
            className='absolute inset-0 opacity-20'
            style={{
              backgroundImage: `url('${BgLatto}')`,
              backgroundBlendMode: 'multiply',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          ></div>
          <div className='relative p-3'>
            <button
              onClick={onClose} // Close the entire popup
              className='absolute right-3 top-3 text-gray-400 hover:text-white z-10'
              aria-label='Close'
            >
              <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>
            </button>
            <h3 className="text-2xl font-semibold text-white mb-6 text-center">Select Payment Method</h3>
            
            {/* Info Message when phmpay is available but others are not */}
            {shouldShowInfoMessage && (
              <div className="mb-4 bg-blue-500/20 border border-blue-400/50 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-blue-100 font-medium mb-1">For GCash, Maya, & GrabPay Users</p>
                    <p className="text-xs text-blue-200/90">
                      Please use <span className="font-semibold">{alternativeMethodNames}</span> below to pay with your GCash, Maya, GrabPay, or QRPh wallet — all in one convenient option!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isLoadingGateways ? (
                <div className="flex justify-center items-center h-32">
                    <Spinner />
                </div>
            ) : availableGateways.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {availableGateways.map((key) => {
                  const config = PAYMENT_METHODS_CONFIG[key];
                  if (!config) return null; // Should not happen if filtered correctly
                  const { Logo, label } = config;
                  const minAmount = gatewayMinAmounts[key] || config.defaultMinAmount || 50;
                  const isFeatured = config.cardVariant === 'featured';
                  const labelColor = config.textColor || (isFeatured ? '#FFFFFF' : '#4b5563');
                  return (
                    <button
                      key={key}
                      onClick={() => handleGatewaySelect(key)}
                      className={getGatewayCardClassName(config)}
                      style={getGatewayCardStyle(config)}
                    >
                      {isFeatured && (
                        <div
                          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/25 via-transparent to-black/10"
                          aria-hidden="true"
                        />
                      )}
                      <div
                        className={`relative z-10 mb-2 flex items-center justify-center ${
                          isFeatured ? 'h-14 w-14 rounded-2xl bg-white/90 shadow-md' : ''
                        }`}
                      >
                        <Logo className={isFeatured ? 'h-10 w-10' : 'mb-0 h-16 w-16'} />
                      </div>
                      <span
                        style={{ color: labelColor }}
                        className={`relative z-10 text-sm font-semibold ${!config.textColor && !isFeatured ? 'text-gray-600' : ''}`}
                      >
                        {label}
                      </span>
                      <span
                        style={{ color: labelColor }}
                        className={`relative z-10 mt-1 text-xs ${!config.textColor && !isFeatured ? 'text-gray-500' : 'opacity-90'}`}
                      >
                        Min: ₱{minAmount.toLocaleString()}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
                <p className="text-gray-400 text-center py-4">No payment methods available.</p>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Main Deposit Form (Amount Input) - Only show if gateway is selected
  if (selectedGateway && !isLoadingGateways) {
    const selectedConfig = PAYMENT_METHODS_CONFIG[selectedGateway];
    const { Logo, label } = selectedConfig;
    
    return (
      <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 font-poppins'>
        <div className='animate-slideInAndShake relative w-full max-w-sm overflow-hidden rounded-2xl transform transition-all duration-300'>
          {/* Background with gradient */}
          <div
            className='absolute inset-0 '
            style={{
              backgroundImage: `url('${BgLatto}'), linear-gradient(108.9deg, rgba(14, 100, 209, 0.6) 0%, rgba(30, 66, 154, 0.6) 100.79%)`,
              backgroundBlendMode: 'multiply',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          ></div>

          {/* Content */}
          <div className='relative p-4'>
            <button
              onClick={onClose}
              className='absolute right-2 top-2 text-white/80 hover:text-white'
              aria-label='Close'
            >
              <svg
                className='w-5 h-5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>

            {/* Selected Payment Method Display */}
            <div className='mb-4 flex items-center justify-center text-white'>
              <button
                onClick={() => {
                  setSelectedGateway(null);
                  setShowGatewaySelection(true);
                }}
                className='flex items-center gap-2 bg-white/20 rounded-lg px-3 py-2 hover:bg-white/30 transition-colors'
              >
                <Logo className="w-6 h-6" />
                <span className="font-medium">{label}</span>
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                </svg>
              </button>
            </div>

            {/* Balance display */}
            <div className='mb-3 flex justify-center items-center text-white'>
              <White />
              <div className='flex flex-col'>
                <h5 className='text-[10px] leading-none font-normal'>
                  Current Balance
                </h5>
                <h3 className='text-4xl font-semibold'>
                  {currentBalance.toLocaleString()}
                </h3>
              </div>
            </div>

            {/* Amount input */}
            <div className='mb-6 relative'>
              <input
                type='number'
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className='w-full bg-white rounded-lg py-3 px-4 pl-12 text-lg font-normal text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                placeholder={`Enter amount (min ₱${getCurrentMinAmount().toLocaleString()})`}
              />
              <Wallet className='absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400' />
            </div>

            {/* Deposit Button */}
            <button
              onClick={handleDepositButtonClick}
              className='w-full bg-[#0E64D1] hover:bg-blue-600 text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2'
            >
              <Withdraw />
              DEPOSIT
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If no gateway selected and not loading, return null (gateway selection will show)
  return null;
}
